import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';

const RESOURCE_FIELDS = [
  { field: "resource_name", label: "Resource Name", required: true },
  { field: "organization", label: "Organization", required: true },
  { field: "primary_sdoh_category", label: "Primary SDOH Category", required: true },
  { field: "secondary_sdoh_categories", label: "Secondary Categories", required: false },
  { field: "client_facing_description", label: "Description", required: true },
  { field: "populations_served", label: "Populations Served", required: false },
  { field: "eligibility_requirements", label: "Eligibility", required: false },
  { field: "cost_to_client", label: "Cost", required: false },
  { field: "service_modality", label: "Service Type", required: false },
  { field: "neighborhoods_served", label: "Neighborhoods", required: false },
  { field: "zip_codes", label: "Zip Codes", required: false },
  { field: "languages_offered", label: "Languages", required: false },
  { field: "physical_address", label: "Address", required: false },
  { field: "phone_number", label: "Phone", required: false },
  { field: "website", label: "Website", required: false },
  { field: "hours", label: "Hours", required: false },
  { field: "how_to_access", label: "How to Access (Staff)", required: false },
  { field: "referral_required", label: "Referral Required", required: false },
  { field: "warm_handoff_possible", label: "Warm Handoff", required: false },
  { field: "known_barriers", label: "Known Barriers (Staff)", required: false },
  { field: "staff_tips", label: "Staff Tips", required: false },
];

const MappingStep = ({
  analysisResult, mappings, onUpdateMapping,
  defaultNeighborhood, setDefaultNeighborhood, neighborhoods,
  loading, onBack, onPreview
}) => {
  return (
    <div className="space-y-6 animate-fade-in">
      <Alert className="bg-[#5C4023]/10 border-[#5C4023]/20">
        <Sparkles className="w-4 h-4 text-[#5C4023]" />
        <AlertDescription className="text-[#5C4023]">
          AI has suggested mappings for your columns. Review and adjust as needed.
        </AlertDescription>
      </Alert>

      <Card className="rounded-2xl border-[#E0D6C8]">
        <CardHeader>
          <CardTitle className="text-xl font-['Manrope']">Column Mappings</CardTitle>
          <CardDescription>Found {analysisResult.total_rows} rows. Map your columns to resource fields.</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-4">
              {analysisResult.all_columns.map((column) => {
                const mapping = mappings.find(m => m.source_column === column);
                return (
                  <div key={column} className="flex items-center gap-4 p-3 bg-[#F3EFE9]/50 rounded-xl">
                    <div className="flex-1">
                      <p className="font-medium text-[#1A1A1A] text-sm">{column}</p>
                      <p className="text-xs text-[#888888] truncate">
                        Sample: {analysisResult.sample_data[0]?.[column] || 'N/A'}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#888888] flex-shrink-0" />
                    <div className="w-48">
                      <Select
                        value={mapping?.target_field || ''}
                        onValueChange={(v) => onUpdateMapping(column, v)}
                      >
                        <SelectTrigger data-testid={`mapping-select-${column}`} className="rounded-lg bg-white">
                          <SelectValue placeholder="Select field..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__skip__">Don&apos;t import</SelectItem>
                          {RESOURCE_FIELDS.map(f => (
                            <SelectItem key={f.field} value={f.field}>
                              {f.label} {f.required && '*'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {mapping?.confidence > 0.8 && (
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">AI Match</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <div className="mt-4 p-4 bg-[#F3EFE9]/50 rounded-xl">
            <Label className="text-sm font-medium text-[#1A1A1A] mb-2 block">
              Default Neighborhood for All Imports
            </Label>
            <p className="text-xs text-[#888888] mb-2">
              If your file doesn&apos;t have a neighborhood column, set a default here. AI will also auto-fill SDOH categories and organizations.
            </p>
            <Select value={defaultNeighborhood} onValueChange={(v) => setDefaultNeighborhood(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="default-neighborhood-select" className="rounded-lg bg-white">
                <SelectValue placeholder="No default (leave empty)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No default</SelectItem>
                {neighborhoods.map(n => (<SelectItem key={n} value={n}>{n}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={onBack} className="flex-1 rounded-full">Back</Button>
            <Button onClick={onPreview} disabled={loading || mappings.length === 0} data-testid="preview-btn" className="flex-1 bg-[#5C4023] hover:bg-[#4A331C] rounded-full">
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> AI Enriching...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Preview &amp; AI Enrich</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MappingStep;
